import { useCallback, useState } from "react";
import { api } from "../../lib/api";
import useAdminLoad, { payloadShapeError } from "../../hooks/useAdminLoad";
import { AFFILIATE_PATHS } from "../../affiliate/contract";

const isObject = (v) => !!v && typeof v === "object" && !Array.isArray(v);

/**
 * `GET /api/tenant/affiliate` — everything the practitioner's page renders, in one read.
 *
 * The shape check is not decoration. Every block below is a POSITIVE CLAIM the screen
 * makes about the practitioner's own money (what they have earned, what is still held,
 * whether a withdrawal is even possible), and a partial payload rendered with `?.`
 * fallbacks would state zeros and "not ready" for facts nobody read. `payout` is checked
 * down to its boolean for exactly that reason: the contract says a client must not read an
 * ABSENT `destinationReady` as "not ready".
 */
export default function useAffiliateDashboard() {
  const [dashboard, setDashboard] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(AFFILIATE_PATHS.tenant);
    if (!isObject(data) || !isObject(data.account) || !isObject(data.account.shareLinks)
        || !isObject(data.program) || !isObject(data.program.rates)
        || !isObject(data.stats) || !isObject(data.stats.referrals)
        || !isObject(data.stats.commissions)
        || !isObject(data.payout) || typeof data.payout.destinationReady !== "boolean") {
      throw payloadShapeError();
    }
    setDashboard(data);
  }, []);

  const { loading, loadError, reload } = useAdminLoad(load);

  /** Fold the 200 of `PUT /code` back in, so the share links update without a second read. */
  const applyAccount = useCallback((account) => {
    setDashboard((d) => (d ? { ...d, account } : d));
  }, []);

  return { dashboard, loading, loadError, reload, applyAccount };
}
